import {Component, Inject, OnInit} from '@angular/core';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {LastModifiedCardUtils} from '@application/helper/last-modified-card-utils';
import {PageUrls} from '@domain/textile-data/page-urls';
import {GetAllWeaveProductsParameters, WEAVE_PRODUCT, WeaveProducts} from '@infrastructure/http/weave-product/weave-products.interface';
import {TextileDataType} from '@presentation/pages/textile-data/textile-data-type.enum';
import {
  AgGridUtils,
  BaseComponent,
  ColDefBuilderFactoryService,
  LinkIconRendererComponent,
  MobileColDef,
  RESPONSIVENESS_VIEW_MODE,
  ResponsivenessViewMode,
  StringUtils,
  Unit
} from '@vdw/angular-component-library';
import {CellClassParams, ICellRendererParams} from 'ag-grid-community';
import {L10nIntlService} from 'angular-l10n';
import {isEmpty} from 'lodash-es';
import {takeUntil} from 'rxjs/operators';
import {OverviewListWeaveStructure} from '../../weave-structure/overview/overview-list-weave-structure';
import {OverviewListWeaveProduct} from './overview-list-weave-product';

@Component({
  templateUrl: './weave-product-overview-page.component.html'
})
export class WeaveProductOverviewPageComponent extends BaseComponent implements OnInit {
  public listOfWeaveProducts: OverviewListWeaveProduct[];

  public readonly textileDataType = TextileDataType;
  public readonly GRID_IDENTIFIER = GridIdentifier.WEAVE_PRODUCT_OVERVIEW;

  private pageUrls: PageUrls;

  public constructor(
    @Inject(WEAVE_PRODUCT) public readonly weaveProducts: WeaveProducts,
    @Inject(RESPONSIVENESS_VIEW_MODE) private responsivenessViewMode: ResponsivenessViewMode,
    private l10nIntlService: L10nIntlService,
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.pageUrls = LastModifiedCardUtils.getPageUrls('weaveProduct');
    this.getWeaveProducts();
  }

  public getColumnDefsForListOfWeaveProducts(): MobileColDef[] {
    return [
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('name', true)
        .withHeaderName('GENERAL.NAME')
        .withComparator(StringUtils.stringComparator)
        .withCellRenderer(LinkIconRendererComponent, (params: ICellRendererParams) => {
          return {
            params,
            canClick: !this.isMobile(),
            editPageUrl: this.pageUrls.edit
          };
        })
        .withCellClass((cellClassParams: CellClassParams) => {
          const result = [];

          const weaveStructure: OverviewListWeaveStructure = cellClassParams.data;
          if (isEmpty(weaveStructure.name)) {
            result.push('object-without-name');
          }
          return result;
        })
        .build(),
      this.colDefBuilderFactoryService.getBuilder().withColIdAndField('description', true).withHeaderName('GENERAL.DESCRIPTION').build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('version')
        .withHeaderName('GENERAL.VERSION')
        .withCellRenderer((params: ICellRendererParams) => params.getValue())
        .withCellClass('right')
        .withPinned(false)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('widthInCm')
        .withHeaderName('GENERAL.DIMENSIONS.WIDTH')
        .withCellRenderer((params: ICellRendererParams) => this.buildAgGridCellTextWithUnit(params.getValue(), Unit.CENTIMETER))
        .withCellClass('right')
        .withPinned(false)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('pickDensityInPicksPerCm')
        .withHeaderName('TEXTILE_DATA.WEAVE_PRODUCT.PICK_DENSITY')
        .withCellRenderer((params: ICellRendererParams) => this.buildAgGridCellTextWithUnit(params.value, Unit.PICKS_PER_CENTIMETER))
        .withCellClass('right')
        .withPinned(false)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('standardSpeedInRotationsPerMinute')
        .withHeaderName('TEXTILE_DATA.WEAVE_PRODUCT.STANDARD_SPEED')
        .withCellRenderer((params: ICellRendererParams) => this.buildAgGridCellTextWithUnit(params.value, Unit.ROTATIONS_PER_MINUTE))
        .withCellClass('right')
        .withPinned(false)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('standardEfficiencyInPercentage')
        .withHeaderName('TEXTILE_DATA.WEAVE_PRODUCT.STANDARD_EFFICIENCY')
        .withCellRenderer((params: ICellRendererParams) => (params.value ? `${params.value} %` : ''))
        .withCellClass('right')
        .withPinned(false)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('numberOfFeeders')
        .withHeaderName('TEXTILE_DATA.WEAVE_PRODUCT.NUMBER_OF_FEEDERS')
        .withCellClass('right')
        .withPinned(false)
        .build(),
      this.colDefBuilderFactoryService.getBuilder().withColIdAndField('numberOfShafts').withHeaderName('TEXTILE_DATA.WEAVE_PRODUCT.NUMBER_OF_SHAFTS').withCellClass('right').withPinned(false).build()
    ];
  }

  public showOnlyLatestVersionChanged(showOnlyLatestVersion: boolean): void {
    this.getWeaveProducts(showOnlyLatestVersion);
  }

  private getWeaveProducts(showOnlyLatestVersion: boolean = true): void {
    const parameters: Partial<GetAllWeaveProductsParameters> = {showOnlyLatestVersion};

    this.weaveProducts
      .getAll(parameters)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((listOfWeaveProducts: OverviewListWeaveProduct[]) => {
        this.listOfWeaveProducts = listOfWeaveProducts;
      });
  }

  private isMobile(): boolean {
    return this.responsivenessViewMode.isPhone;
  }

  private buildAgGridCellTextWithUnit(value: number, defaultUnit: Unit): string {
    return AgGridUtils.buildAgGridCellTextWithUnit(value, defaultUnit, this.l10nIntlService);
  }
}
